import { useEffect, useRef, useState } from "react";
import { TENANT_TYPE, USER_ROLES } from "./constants/userConstants";
import CryptoJS from "crypto-js";
import { APP_DETAILS } from "../config/version";
import { backendConfig } from "../config/BackendConfig";
import { stringToArray } from "cron-converter";
import parser from "cron-parser";

export const roundToTwoDec = (val) => {
  if (val || val === 0) {
    return Math.round(val * 100) / 100;
  }
  return "---";
};

export const formatAMPM = (date, addSecond = true) => {
  const time = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
  });
  let [hh, mm, ss] = time.split(":");
  let [sss, ampm] = ss.split(" ");
  if (hh.length === 1) {
    hh = "0" + hh;
  }
  if (mm.length === 1) {
    mm = "0" + mm;
  }
  if (sss.length === 1) {
    sss = "0" + sss;
  }
  if (addSecond) {
    return hh + ":" + mm + ":" + sss + " " + ampm;
  } else {
    return hh + ":" + mm + " " + ampm;
  }
};

export const tryParseDateFromString = (
  dateStringCandidateValue,
  format = "ymd"
) => {
  const d = new Date(dateStringCandidateValue);
  const time = d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  const dformat =
    [d.getMonth() + 1, d.getDate(), d.getFullYear()].join("/") + " " + time;

  return dformat;
};

export const formatDateTime = (
  date,
  monthInNumeric = true,
  seconds = false
) => {
  const monthArr = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  let day = date.getDate();
  let month = date.getMonth();
  let year = date.getFullYear();
  let hhmmss = formatAMPM(date, seconds);
  day = String(day).padStart(2, "0");
  if (monthInNumeric) {
    month = String(month + 1).padStart(2, "0");
    return year + "/" + month + "/" + day + " " + hhmmss;
  }
  return day + "-" + monthArr[month] + "-" + year + " " + hhmmss;
};

export const checkActiveTab = (link, checkDeviceView = false) => {
  const deviceViewRegex = new RegExp("^/device-view");

  const pathName = window.location.pathname;

  if (checkDeviceView) {
    return deviceViewRegex.test(pathName);
  }
  return pathName === link;
};

/**
 *
 * @param {*} timeString : example [2023-05-17T08:08:52.314Z]
 * @returns time : example [2023/05/17 01:38:34 PM]
 */
export const timeStringToTimeAMPM = (timeString) => {
  if (timeString) {
    const currentDateTime = new Date(timeString);
    const year = currentDateTime.getFullYear();
    const month = ("0" + (currentDateTime.getMonth() + 1)).slice(-2);
    const date = ("0" + currentDateTime.getDate()).slice(-2);
    // const month = currentDateTime.getMonth();
    // const date = currentDateTime.getDate();
    let hhmmss = formatAMPM(currentDateTime, true);
    return `${year}/${month}/${date} ${hhmmss}`;
  }
  return "---";
};

export const round5 = (value) => {
  return value % 5 >= 2.5
    ? parseInt(value / 5) * 5 + 5
    : parseInt(value / 5) * 5;
};

export const round5ForCatridge = (value) => {
  const integerPart = parseInt(value);

  if (integerPart === 100 || integerPart === 0) {
    return integerPart;
  } else if (integerPart > 100) {
    return 100;
  } else if (integerPart % 5 === 0 && integerPart !== 0) {
    return integerPart;
  } else {
    return Math.floor(integerPart / 5) * 5;
  }
};

export const getUserRole = (userRole, tenantType) => {
  if (tenantType === TENANT_TYPE.Corporate) {
    switch (userRole) {
      case "Super Admin":
        return USER_ROLES.superAdminCorp;
      case "Admin":
        return USER_ROLES.adminCorp;
      case "User":
        return USER_ROLES.userCorp;
      case "View Only User":
        return USER_ROLES.viewOnlyUserCorp;
      default:
        return USER_ROLES.userCorp;
    }
  }
  return USER_ROLES.userCorp;

  /**For Future implementation */

  // if (tenantType === TENANT_TYPE.Retail) {
  //   switch (userRole) {
  //     case "Admin":
  //       return USER_ROLES.adminRetail;
  //     case "User":
  //       return USER_ROLES.userRetail;
  //     case "View Only User":
  //       return USER_ROLES.viewOnlyUserRetail;
  //     default:
  //       return USER_ROLES.userRetail;
  //   }
  // }
  // if (tenantType === TENANT_TYPE.Chorus) {

  // }
};

export const useEffectOnce = (effect) => {
  const effectFn = useRef(effect);
  const destroyFn = useRef();
  const effectCalled = useRef(false);
  const rendered = useRef(false);
  const [, setVal] = useState(0);

  if (effectCalled.current) {
    rendered.current = true;
  }

  useEffect(() => {
    // only execute the effect first time around
    if (!effectCalled.current) {
      destroyFn.current = effectFn.current();
      effectCalled.current = true;
    }

    // this forces one render after the effect is run
    setVal((val) => val + 1);

    return () => {
      // if the comp didn't render since the useEffect was called,
      // we know it's the dummy React cycle
      if (!rendered.current) {
        return;
      }

      // otherwise this is not a dummy destroy, so call the destroy func
      if (destroyFn.current) {
        destroyFn.current();
      }
    };
  }, []);
};

export const getEstimatedTimeElapsed = (timestamp) => {
  const MINUTE = 60 * 1000; // milliseconds in a minute
  const HOUR = 60 * MINUTE; // milliseconds in an hour
  const DAY = 24 * HOUR; // milliseconds in a day

  const currentTime = new Date().getTime(); // current time in milliseconds
  const timeElapsed = currentTime - new Date(timestamp).getTime(); // time elapsed in milliseconds

  if (timeElapsed < 5 * MINUTE) {
    return "just now";
  } else if (timeElapsed < 10 * MINUTE) {
    return "5 mins ago";
  } else if (timeElapsed < 20 * MINUTE) {
    return "10 mins ago";
  } else if (timeElapsed < 30 * MINUTE) {
    return "20 mins ago";
  } else if (timeElapsed < 40 * MINUTE) {
    return "30 mins ago";
  } else if (timeElapsed < 50 * MINUTE) {
    return "40 mins ago";
  } else if (timeElapsed < HOUR) {
    return "50 mins ago";
  } else if (timeElapsed < 2 * HOUR) {
    return "1 hour ago";
  } else if (timeElapsed < 3 * HOUR) {
    return "2 hours ago";
  } else if (timeElapsed < 24 * HOUR) {
    const hoursElapsed = Math.floor(timeElapsed / HOUR);
    return `${hoursElapsed} hours ago`;
  } else {
    return "Invalid timestamp"; // handle invalid timestamp
  }
};

/**
 * Hook that alerts clicks outside of the passed ref
 */
export const useOutsideAlerter = (ref, clickedOutsideFunction, btnRef) => {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        event.target.id !== btnRef.current.id
      ) {
        clickedOutsideFunction();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, clickedOutsideFunction]);
};

export const getWifiStrength = (strength) => {
  if (strength < -29 && strength > -67) {
    return "Excellent";
  } else if (strength < -66 && strength > -70) {
    return "VeryGood";
  } else if (strength < -69 && strength > -80) {
    return "Okey";
  } else if (strength < -79 && strength > -90) {
    return "Poor";
  } else {
    return "Poor";
  }
};

export const decryptToken = (skey, secretKey) => {
  /**Decrypt the skey[from cognito details] using secretKey */
  const key = CryptoJS.PBKDF2(secretKey, secretKey, {
    keySize: 256 / 32,
  });
  const iv = CryptoJS.PBKDF2(secretKey, secretKey, {
    keySize: 128 / 32,
  });
  let decryptedToken = CryptoJS.AES.decrypt(skey, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString(CryptoJS.enc.Utf8);
  decryptedToken = JSON.parse(decryptedToken);
  return decryptedToken;
};

export const encryptToken = (decryptedSKey) => {
  const currentTime = new Date().toUTCString();

  const key = CryptoJS.PBKDF2(decryptedSKey.token, decryptedSKey.token, {
    keySize: 256 / 32,
  });

  const iv = CryptoJS.PBKDF2(decryptedSKey.token, decryptedSKey.token, {
    keySize: 128 / 32,
  });

  const newtoken = CryptoJS.AES.encrypt(currentTime, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
  return newtoken;
};

export const formatDate = (
  dateToFormat,
  isDayOfWeek = true,
  isSeconds = false
) => {
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const date = new Date(dateToFormat);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const dayOfWeek = daysOfWeek[date.getDay()];
  let hours = date.getHours();
  let secs = date.getSeconds();
  const ampm = hours >= 12 ? "PM" : "AM";

  // Convert hours from 24-hour format to 12-hour format
  const formattedHours = hours % 12 === 0 ? 12 : hours % 12;

  const minutes = date.getMinutes();

  if (isDayOfWeek) {
    return `${year}/${month}/${day} ${dayOfWeek} ${formattedHours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  } else if (isSeconds) {
    return `${year}/${month}/${day} ${formattedHours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")} ${ampm}`;
  } else {
    return `${year}/${month}/${day} ${formattedHours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  }
};

export const envDetailsChecker = () => {
  let env = backendConfig.CURRENT_ENV;
  return APP_DETAILS.find((item) => item.environment === env);
};

export const valueFinderByModes = (
  mode = "",
  valueType = "",
  value,
  symbols = "true"
) => {
  if ((mode === "OFFLINE" || mode === "STANDBY") && valueType !== "cartridge") {
    return "---";
  } else if (valueType === "cartridge" || value === 0) {
    return value || value === 0
      ? `${round5ForCatridge(value)}${symbols ? "%" : ""}`
      : "---%";
  } else if (valueType === "humidity") {
    return value || value === 0
      ? `${Math.round(value)}${symbols ? "%" : ""}`
      : "---%";
  } else if (valueType === "temperature") {
    return value || value === 0
      ? `${Math.round(value)}${symbols ? "°C" : ""}`
      : "---°C";
  } else if (valueType === "ppb") {
    return value || value === 0 ? Math.max(Math.round(value), 0) : "---";
  }
};

export const modeChecker = (mode) => {
  if (mode === "OFFLINE" || mode === "STANDBY") {
    return true;
  }
  return false;
};

export const cartridgeColorFinder = (value, mode, page) => {
  let result = round5ForCatridge(value);

  if (result === 0 && mode !== "OFFLINE") {
    return "zero";
  } else if ((result <= 19) & (page === "dashboard")) {
    return "active";
  } else if ((result > 19) & (page === "dashboard")) {
    return "active rangeLabel2";
  } else if ((result <= 15) & (page === "deviceView")) {
    return "active";
  }
};

export const getTaskFormattedTime = (cronExpression, recurrenceType) => {
  if (recurrenceType === "DAILY") {
    try {
      const interval = parser.parseExpression(cronExpression);
      const nextDate = interval.next();
      const hours = nextDate.getHours();
      const minutes = nextDate.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
      const formattedTime = `${formattedHours
        .toString()
        .padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
      return `Everyday at ${formattedTime}`;
    } catch (error) {
      return "Invalid cron expression";
    }
  } else {
    try {
      const parts = cronExpression.split(" ");
      let selectedDays = stringToArray(cronExpression)[4]; // Parse the days part

      selectedDays = selectedDays.sort((a, b) => a - b);
      const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      // Convert selected day numbers to abbreviations
      const selectedDayAbbreviations = selectedDays
        .map((day) => {
          return daysOfWeek[parseInt(day, 10)];
        })
        .join(", ");

      const hours = parts[1];
      const minutes = parts[0];
      const ampm = hours >= 12 ? "PM" : "AM";

      // Convert hours from 24-hour format to 12-hour format
      const formattedHours = hours % 12 === 0 ? 12 : hours % 12;

      const formattedTime = `${formattedHours
        .toString()
        .padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;

      if (parts[4] === "*") {
        return `Everyday at ${formattedTime}`;
      }
      return `Every ${selectedDayAbbreviations} at ${formattedTime}`;
    } catch (error) {
      return "Invalid Cron Expression";
    }
  }
};

export const takeScreenShot = (screenShotRef) => {
  const { cropPositionTop, cropPositionLeft, cropWidth, cropHeigth } = {
    cropPositionTop: 0,
    cropPositionLeft: 0,
    cropWidth: screenShotRef.current.offsetWidth,
    cropHeigth: screenShotRef.current.offsetHeight,
  };
  return { cropPositionTop, cropPositionLeft, cropWidth, cropHeigth };
};

export const handleTableColSort = (key, columList) => {
  let sortColumnIndex = columList.findIndex((item) => item.key === key);
  let listCopy = [...columList];
  listCopy[sortColumnIndex].clickCount =
    (listCopy[sortColumnIndex].clickCount + 1) % 3; //click count can be 0,1or2 . 0-->1, 1-->2, 2-->0

  listCopy[sortColumnIndex].val =
    listCopy[sortColumnIndex].clickCount === 2
      ? ""
      : listCopy[sortColumnIndex].clickCount === 1
      ? "asc"
      : "desc";
  return listCopy;
};

export const extractVersion = (versionString = "") => {
  if (!versionString) return "";
  const parts = versionString.split("-"); // Split the string by `-`
  const version = parts.slice(0, 2).join("-"); // Join the first two parts with `-`

  return version;

  // const regex = /v(\d+\.\d+\.\d+)/;
  // const match = versionString.match(regex);
  // return match ? match[1] : null;
};

export const millisToMinutesAndSeconds = (millis) => {
  var minutes = Math.floor(millis / 60000);
  var seconds = ((millis % 60000) / 1000).toFixed(0);
  // return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
  return minutes;
};



export const trimText = (filename, maxLength=31) => {
  const extensionIndex = filename.lastIndexOf('.');
  
  // Split the filename into name and extension
  const name = filename.substring(0, extensionIndex);
  const extension = filename.substring(extensionIndex);
  
  if (name.length > maxLength) {
    return name.substring(0, maxLength) + '...' + name.charAt(name.length - 1) + extension;
  }
  
  return filename;
}
